import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChange,
    SimpleChanges
} from '@angular/core';
import * as _ from 'lodash-es';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {PaginationInfo} from '../../models/pagination-info';
import {
    PAGINATION_BEGIN_PAGE_COUNT,
    PAGINATION_CURRENT_AROUND_PAGE_COUNT,
    PAGINATION_DEFAULT_PAGE_SIZE,
    PAGINATION_END_PAGE_COUNT,
    PAGINATION_FIRST_PAGE
} from './pagination-constants';

const PAGINATION_DEFAULT_PAGE_SIZE_LIST = [5, 10, 15, 20, 25];

interface PageLinksGroup {
    start: number;
    count: number;
}

interface PaginationInputChanges extends SimpleChanges {
    paginationInfo: SimpleChange;
}

@Component({
    selector: 'table-pagination',
    templateUrl: './table-pagination.component.html',
    styleUrls: ['./table-pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TablePaginationComponent implements OnChanges {

    public static setPageSize({oldPageSize, newPageSize, oldPageIndex, listFilter}: {
        oldPageSize: number;
        newPageSize: number;
        oldPageIndex: number;
        listFilter: CommonListFilter;
    }): boolean {
        let isUpdated: boolean = false;

        if (newPageSize !== oldPageSize) {

            // Adjust the pageIndex according to the new pageSize.
            if (oldPageIndex) {
                const anchorRowIndex = oldPageIndex * oldPageSize;

                listFilter.pageIndex = Math.trunc(anchorRowIndex / newPageSize);
            }

            listFilter.pageSize = newPageSize;

            isUpdated = true;
        }

        return isUpdated;
    }

    @Input()
    public filter: CommonListFilter;

    @Input()
    public paginationInfo: PaginationInfo;

    @Output()
    public filterChange = new EventEmitter<CommonListFilter>();

    public pageSize: number;
    public pageCount: number;
    public currentPage: number = PAGINATION_FIRST_PAGE;
    public pageSizeList: number[];
    public pageLinks: number[]; // nulls considered to be ellipsis

    public ngOnChanges(changes: PaginationInputChanges): void {
        if (changes.paginationInfo) {
            this.onPaginationInfoChange();
        }
    }

    public setPageIndex(pageIndex: number): void {
        const pageCount: number = this.pageCount || PAGINATION_FIRST_PAGE;

        if (pageIndex >= PAGINATION_FIRST_PAGE && pageIndex < pageCount && this.currentPage !== pageIndex) {
            this.currentPage = this.filter.pageIndex = this.paginationInfo.pageIndex = pageIndex;
            this.updatePageRequests();
            this.filterChanged();

            this.updatePageLinks();
        }
    }

    public setPageSize(pageSize: number): void {
        const isUpdated = TablePaginationComponent.setPageSize({
            oldPageSize:  this.pageSize,
            newPageSize:  pageSize,
            oldPageIndex: this.currentPage,
            listFilter:   this.filter
        });

        if (isUpdated) {
            this.filterChanged();
        }
    }

    private onPaginationInfoChange(): void {
        // In case of pagination info is temporary undefined
        const paginationInfo: PaginationInfo = this.paginationInfo || {};

        this.pageSize = paginationInfo.pageSize || PAGINATION_DEFAULT_PAGE_SIZE;
        this.currentPage = paginationInfo.pageIndex || PAGINATION_FIRST_PAGE;
        this.pageCount = paginationInfo.pageCount && paginationInfo.pageIndex
            ? paginationInfo.pageCount + paginationInfo.pageIndex
            : paginationInfo.pageCount || 0;
        this.pageSizeList = PAGINATION_DEFAULT_PAGE_SIZE_LIST;

        this.updatePageLinks();
    }

    private filterChanged(): void {
        this.filterChange.emit(this.filter);
    }

    /**
     * To display the first N pages at least M pages on the right side
     * +1 in order to include the current page in the count
     */
    private updatePageRequests(): void {
        this.filter.pageCount = Math.max(
            // to display the first N pages
            PAGINATION_BEGIN_PAGE_COUNT + PAGINATION_CURRENT_AROUND_PAGE_COUNT * 2 - this.currentPage - 1,
            PAGINATION_CURRENT_AROUND_PAGE_COUNT // at least M pages on the right side
        ) + 1; // +1 in order to include the current page in the count
    }

    /**
     * Fits pages groups bounds to the available pages range, and joins successive groups.
     * Mutates the passed groups array.
     * @param {PageLinksGroup[]} groups
     * @returns {PageLinksGroup[]}
     */
    private fixPageLinkGroups(groups: PageLinksGroup[]): PageLinksGroup[] {
        let prevGroup;
        let iGroup = 0;

        while (iGroup < groups.length) {
            const group = groups[iGroup];

            // Fix the group's bounds
            group.start = Math.min(group.start, this.pageCount);
            group.count = (group.start + group.count - 1 > this.pageCount)
                ? this.pageCount - group.start + 1
                : group.count;

            if (prevGroup && prevGroup.start + prevGroup.count >= group.start) {
                prevGroup.count = group.start + group.count - prevGroup.start; // join the group to the previous group
                groups.splice(iGroup, 1); // remove the redundant group
            } else {
                prevGroup = group;
                iGroup++;
            }
        }

        return groups;
    }

    private updatePageLinks(): void {
        const pageCount = this.pageCount;

        let middleStart = Math.min(
            this.currentPage - PAGINATION_CURRENT_AROUND_PAGE_COUNT,
            pageCount - PAGINATION_CURRENT_AROUND_PAGE_COUNT * 2 - 1
        );
        middleStart = Math.max(middleStart, 0);
        const middleCount = Math.min(PAGINATION_CURRENT_AROUND_PAGE_COUNT * 2 + 1, pageCount - middleStart + 1);

        const pageLinksGroups = this.fixPageLinkGroups([
            {start: 0, count: PAGINATION_BEGIN_PAGE_COUNT},
            {start: middleStart, count: middleCount},
            {start: pageCount - PAGINATION_END_PAGE_COUNT, count: PAGINATION_END_PAGE_COUNT}
        ]);

        this.pageLinks = _.transform(pageLinksGroups, (pagesLinks, group: PageLinksGroup, groupIndex) => {
            if (groupIndex) {

                // add ellipsis separator
                pagesLinks.push(null);
            }

            pagesLinks.push.apply(pagesLinks, _.range(group.start, group.start + group.count));
        }, []);
    }
}
